/**
 * Print-ready invoice generator for Asvasa orders.
 * Opens a new window with A4-friendly, brand-styled invoice and auto-triggers print().
 */

const BRAND = {
  name: "Asvasa",
  tagline: "Inspired by tradition, crafted for modern men",
  legal: "Asvasa Atelier Pvt. Ltd.",
  address: "Ahmedabad, Gujarat 380007, India",
  email: "asvasaclothing@gmail.com",
  website: "asvasaa.com",
  gstin: "24AMFPY5429E1ZW",
  instagram: "@asvasaa_",
  instagramUrl: "https://instagram.com/asvasaa_",
  facebook: "Asvasaclothing",
  facebookUrl: "https://facebook.com/Asvasaclothing",
};

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function printInvoice(order) {
  if (!order) return;
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;

  const itemsRows = order.items
    .map(
      (it, i) => `
        <tr>
          <td class="num">${i + 1}</td>
          <td>
            <div class="item-name">${esc(it.name)}</div>
            <div class="item-meta">${esc(it.color)} · Size ${esc(it.size)}</div>
          </td>
          <td class="num">${it.qty}</td>
          <td class="num">${INR(it.price)}</td>
          <td class="num">${INR(it.price * it.qty)}</td>
        </tr>`
    )
    .join("");

  const t = order.totals;
  const s = order.shipping;
  const issuedOn = new Date(order.created_at).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const discountRow =
    t.discount_amount > 0
      ? `<tr><td>Discount (${t.discount_percent}%)</td><td class="num">−${INR(t.discount_amount)}</td></tr>`
      : "";

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Invoice ${esc(order.order_number)} · Asvasa</title>
  <style>
    @page { size: A4; margin: 18mm 14mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #f6f4ee; color: #1A1A1A; font-family: Helvetica, Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .sheet { max-width: 760px; margin: 24px auto; background: #fff; padding: 44px 48px 36px; box-shadow: 0 12px 40px rgba(0,0,0,0.06); }
    .serif { font-family: Georgia, "Times New Roman", serif; }

    /* ── Header ── */
    .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1A1A1A; padding-bottom: 22px; }
    .wordmark { font-family: Georgia, serif; font-size: 38px; letter-spacing: 0.32em; color: #1A1A1A; margin: 0; }
    .sub { font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #6b6b6b; margin: 6px 0 0 0; }
    .meta { text-align: right; font-size: 11px; line-height: 1.7; color: #4a4a4a; }
    .meta strong { color: #1A1A1A; font-size: 13px; letter-spacing: 0.04em; }
    .doc-label { font-family: Georgia, serif; font-size: 28px; letter-spacing: 0.24em; color: #1A1A1A; margin: 0 0 6px 0; }

    /* ── Status pill ── */
    .pill { display: inline-block; padding: 4px 10px; font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase; border: 1px solid #1A1A1A; }
    .pill.paid { background: #1A1A1A; color: #c3ba9e; }
    .pill.pending { background: #fff7d1; }
    .pill.refunded, .pill.failed { background: #fde2e2; }

    /* ── Parties ── */
    .parties { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; margin: 28px 0; }
    .party h4 { font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #6b6b6b; margin: 0 0 10px 0; font-weight: 600; }
    .party p { margin: 0; font-size: 12px; line-height: 1.6; color: #1A1A1A; }
    .party .name { font-family: Georgia, serif; font-size: 14px; margin-bottom: 4px; }

    /* ── Items table ── */
    table.items { width: 100%; border-collapse: collapse; margin-top: 6px; }
    table.items thead th { background: #1A1A1A; color: #c3ba9e; font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase; padding: 11px 12px; text-align: left; font-weight: 500; }
    table.items thead th.num { text-align: right; }
    table.items td { padding: 14px 12px; border-bottom: 1px solid #e5e1d4; font-size: 12.5px; vertical-align: top; }
    table.items td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .item-name { font-family: Georgia, serif; font-size: 13.5px; color: #1A1A1A; }
    .item-meta { font-size: 10.5px; color: #7a7a7a; letter-spacing: 0.04em; margin-top: 3px; }

    /* ── Totals ── */
    .totals-wrap { display: flex; justify-content: flex-end; margin-top: 18px; }
    table.totals { width: 280px; border-collapse: collapse; font-size: 12.5px; }
    table.totals td { padding: 6px 0; color: #4a4a4a; }
    table.totals td.num { text-align: right; color: #1A1A1A; font-variant-numeric: tabular-nums; }
    table.totals tr.grand td { border-top: 1px solid #1A1A1A; padding-top: 12px; padding-bottom: 0; font-family: Georgia, serif; font-size: 18px; color: #1A1A1A; }
    table.totals tr.grand td.num { font-size: 22px; }

    /* ── Payment + thank you ── */
    .payment { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px; padding-top: 22px; border-top: 1px solid #e5e1d4; }
    .payment .info { font-size: 12px; color: #4a4a4a; line-height: 1.7; }
    .payment .info strong { color: #1A1A1A; }
    .thanks { text-align: right; max-width: 280px; }
    .thanks h3 { font-family: Georgia, serif; font-size: 22px; margin: 0 0 6px 0; color: #1A1A1A; font-style: italic; }
    .thanks p { font-size: 11px; color: #6b6b6b; margin: 0; line-height: 1.6; }

    /* ── Footer band ── */
    .footer { margin-top: 28px; background: #1A1A1A; color: #c3ba9e; padding: 22px 26px; }
    .footer .row { display: flex; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
    .footer h5 { font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #c3ba9e; opacity: 0.6; margin: 0 0 6px 0; font-weight: 500; }
    .footer p, .footer a { font-size: 11.5px; color: #c3ba9e; line-height: 1.6; text-decoration: none; margin: 0; }
    .footer .brand-end { font-family: Georgia, serif; font-style: italic; font-size: 13px; margin-top: 8px; opacity: 0.85; }

    /* ── Terms ── */
    .terms { font-size: 9.5px; color: #8a8a8a; margin-top: 16px; line-height: 1.7; letter-spacing: 0.02em; }
    .terms strong { color: #1A1A1A; letter-spacing: 0.18em; font-size: 9px; text-transform: uppercase; }

    /* ── Print action bar (hidden on print) ── */
    .actions { max-width: 760px; margin: 0 auto 16px; display: flex; gap: 10px; justify-content: flex-end; padding: 0 4px; }
    .actions button { background: #1A1A1A; color: #c3ba9e; border: none; padding: 10px 22px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; cursor: pointer; font-family: inherit; }
    .actions button.ghost { background: transparent; color: #1A1A1A; border: 1px solid #1A1A1A; }
    @media print {
      .actions { display: none !important; }
      body { background: #fff; }
      .sheet { box-shadow: none; margin: 0; padding: 0; max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="actions">
    <button class="ghost" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print / Save PDF</button>
  </div>

  <div class="sheet">
    <!-- Header -->
    <div class="head">
      <div>
        <h1 class="wordmark">ASVASA</h1>
        <p class="sub">From the Ahmedabad atelier</p>
      </div>
      <div class="meta">
        <p class="doc-label">INVOICE</p>
        <p><strong>${esc(order.order_number)}</strong></p>
        <p>Issued ${esc(issuedOn)}</p>
        <p style="margin-top:8px;"><span class="pill ${esc(order.payment_status)}">${esc(order.payment_status)}</span></p>
      </div>
    </div>

    <!-- Parties -->
    <div class="parties">
      <div class="party">
        <h4>From</h4>
        <p class="name serif">${esc(BRAND.legal)}</p>
        <p>${esc(BRAND.address)}</p>
        <p><strong>GSTIN</strong> ${esc(BRAND.gstin)}</p>
        <p>${esc(BRAND.email)}</p>
        <p>${esc(BRAND.website)}</p>
      </div>
      <div class="party">
        <h4>Bill to</h4>
        <p class="name serif">${esc(s.full_name)}</p>
        <p>${esc(s.email)}</p>
        <p>${esc(s.phone)}</p>
      </div>
      <div class="party">
        <h4>Ship to</h4>
        <p>${esc(s.address_line1)}</p>
        ${s.address_line2 ? `<p>${esc(s.address_line2)}</p>` : ""}
        <p>${esc(s.city)}, ${esc(s.state)} ${esc(s.pincode)}</p>
        <p>${esc(s.country || "India")}</p>
      </div>
    </div>

    <!-- Items -->
    <table class="items">
      <thead>
        <tr>
          <th style="width:32px;">#</th>
          <th>Item</th>
          <th class="num" style="width:56px;">Qty</th>
          <th class="num" style="width:96px;">Unit</th>
          <th class="num" style="width:110px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals-wrap">
      <table class="totals">
        <tr><td>Subtotal</td><td class="num">${INR(t.subtotal)}</td></tr>
        ${discountRow}
        <tr><td>Shipping</td><td class="num">${t.shipping_fee ? INR(t.shipping_fee) : "Complimentary"}</td></tr>
        <tr class="grand"><td>Total</td><td class="num">${INR(t.total)}</td></tr>
      </table>
    </div>

    <!-- Payment + thanks -->
    <div class="payment">
      <div class="info">
        <h4 style="font-size:9px;letter-spacing:0.32em;text-transform:uppercase;color:#6b6b6b;margin:0 0 6px 0;">Payment</h4>
        <p>Method: <strong>${esc(order.payment_method?.toUpperCase())}</strong></p>
        <p>Status: <strong>${esc(order.payment_status)}</strong></p>
        ${order.razorpay_payment_id ? `<p>Ref: <strong>${esc(order.razorpay_payment_id)}</strong></p>` : ""}
        ${order.tracking_number ? `<p>AWB: <strong>${esc(order.tracking_number)}</strong>${order.carrier ? ` · ${esc(order.carrier)}` : ""}</p>` : ""}
      </div>
      <div class="thanks">
        <h3>Thank you for choosing Asvasa.</h3>
        <p>Each piece is hand-finished and packed at our Ahmedabad atelier with care.</p>
      </div>
    </div>

    <!-- Terms -->
    <p class="terms"><strong>Notes</strong> &nbsp;·&nbsp; Shipping is complimentary across India. Returns are accepted within 15 days of delivery for damaged or defective pieces — write to ${esc(BRAND.email)} with photos to initiate. This invoice is computer-generated and does not require a signature.</p>

    <!-- Footer band -->
    <div class="footer">
      <div class="row">
        <div>
          <h5>Stay in touch</h5>
          <p><strong>Instagram</strong> <a href="${esc(BRAND.instagramUrl)}">${esc(BRAND.instagram)}</a></p>
          <p><strong>Facebook</strong> <a href="${esc(BRAND.facebookUrl)}">${esc(BRAND.facebook)}</a></p>
          <p><strong>Email</strong> <a href="mailto:${esc(BRAND.email)}">${esc(BRAND.email)}</a></p>
        </div>
        <div style="text-align:right;">
          <h5>Atelier</h5>
          <p>${esc(BRAND.address)}</p>
          <p>${esc(BRAND.website)}</p>
          <p class="brand-end">${esc(BRAND.tagline)}</p>
        </div>
      </div>
    </div>
  </div>

  <script>
    window.addEventListener("load", () => { setTimeout(() => window.print(), 350); });
  </script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
